import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { useState } from 'react';

interface MFEIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId?: string;
}

export function MFEIntegrationDialog({ open, onOpenChange, workflowId }: MFEIntegrationDialogProps) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null);

  const handleCopy = (text: string, tabName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedTab(tabName);
    setTimeout(() => setCopiedTab(null), 2000);
  };

  const evoSpacesInstructions = `1. Open Evo Spaces
2. Click "Add App" in the top navigation
3. Search for "Workflow Chat"
4. Select and configure with this workflow ID${workflowId ? `:
   ${workflowId}` : ''}
5. The MFE will appear in your Space`;

  const modalCode = `import { openMfe } from '@access-evo/react-app-api';

// Open chat as modal from within an Evo product
openMfe('evoworkflow-chat-mfe', {
  type: 'modal',
  width: '600px',
  parameters: { 
    workflowId: '${workflowId || '[your-workflow-id]'}'
  }
});

// Note: This only works within Evo products (iframed or Evo Spaces)
// For external websites, see the "External Embed" tab`;

  const embedCode = `<!-- Step 1: Add import map to <head> -->
<script type="importmap">
{
  "imports": {
    "react": "https://esm.sh/react@19.2.0",
    "react-dom/client": "https://esm.sh/react-dom@19.2.0/client",
    "i18next": "https://esm.sh/i18next@25.5.2",
    "react-i18next": "https://esm.sh/react-i18next@15.7.3?external=react,i18next"
  }
}
</script>

<!-- Step 2: Initialize MFE host -->
<script type="module">
  import { initialiseMfes } from '@access-evo/mfe-host';
  
  const cleanup = initialiseMfes({
    hostName: 'your-website',
    environment: 'prod',
    geography: 'uk',
    getEvoUser: () => ({
      id: 'user-123',
      emailAddress: 'user@example.com',
      organisation: { id: 'org-1' }
    }),
    getAccessToken: () => 'your-bearer-token'
  });
</script>

<!-- Step 3: Add container and render MFE -->
<div id="chat-container" style="height: 600px;"></div>

<script type="module">
  window.AccessEvo.mfes.add({
    instanceId: 'unique-id-123',
    name: 'evoworkflow-chat-mfe',
    container: document.getElementById('chat-container'),
    parameters: {
      workflowId: '${workflowId || '[your-workflow-id]'}'
    }
  });
</script>

<!-- Requirements:
  - MFE must be published to Evo registry
  - You must handle authentication and provide valid tokens
  - BFF Proxy must be configured for your backend
-->`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>MFE Integration Guide</DialogTitle>
          <DialogDescription>
            Choose how you want to integrate the Workflow Chat MFE into your application
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="spaces" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="spaces">Evo Spaces</TabsTrigger>
            <TabsTrigger value="modal">Modal</TabsTrigger>
            <TabsTrigger value="embed">External Embed</TabsTrigger>
          </TabsList>

          {/* Evo Spaces Tab */}
          <TabsContent value="spaces" className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-2">Add to Evo Spaces</h3>
              <p className="text-sm text-muted-foreground mb-4">
                The easiest way to use this chat is by adding it to an Evo Space.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 border">
              <pre className="text-xs whitespace-pre-wrap font-mono">
                {evoSpacesInstructions}
              </pre>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(evoSpacesInstructions, 'spaces')}
                className="flex-1"
              >
                {copiedTab === 'spaces' ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Instructions
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Modal Tab */}
          <TabsContent value="modal" className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-2">Open as Modal from Evo Product</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Open the chat as a modal dialog from within an Evo product (requires Evo context).
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 border">
              <pre className="text-xs whitespace-pre-wrap font-mono overflow-x-auto">
                {modalCode}
              </pre>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(modalCode, 'modal')}
                className="flex-1"
              >
                {copiedTab === 'modal' ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a
                  href="https://evo-docs.access-workspace.com/docs/mfes/iframe-api#openmfe"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Documentation
                </a>
              </Button>
            </div>
          </TabsContent>

          {/* External Embed Tab */}
          <TabsContent value="embed" className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-2">Embed on External Website</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Embed the chat on any website using this code snippet.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 border">
              <pre className="text-xs whitespace-pre-wrap font-mono overflow-x-auto">
                {embedCode}
              </pre>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(embedCode, 'embed')}
                className="flex-1"
              >
                {copiedTab === 'embed' ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </>
                )}
              </Button>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>⚠️ <strong>Requirements for External Hosting:</strong></p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>MFE must be published to Evo registry</li>
                <li>Import map must be added to HTML head (see code above)</li>
                <li>Must call initialiseMfes() before rendering MFE</li>
                <li>Must provide authentication (getAccessToken callback)</li>
                <li>BFF Proxy must be configured for your backend API</li>
              </ul>
              <p className="mt-2">
                📚 See <a href="https://evo-docs.access-workspace.com/docs/mfes/hosting-mfes" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Hosting MFEs documentation</a> for complete guide.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 pt-4 border-t">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <span>ℹ️</span>
            <p>
              For production deployment, ensure the MFE is published and your BFF Proxy is configured.
              See the <a href="https://evo-docs.access-workspace.com/docs/mfes" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">MFE documentation</a> for details.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
